import ReCAPTCHA from 'react-google-recaptcha';
import React, { useRef, useState } from 'react';
import { useSearchParam } from 'react-use';
import { useTranslation } from 'react-i18next';
import { FiMail } from 'react-icons/fi';

import config from 'config';

import { isStandalone, ua } from '@mssgme/helpers';
import { useActions, useIsNativeApp } from '@mssgme/hooks';
import { Button, FormField, useEnhancedFormik } from '@mssgme/ui';
import { AppleLoginButton, GoogleLoginButton, LegaliseSection, SocialLoginButton } from '../../../UI';

import styles from './styles.scss';

const SHOW_GOOGLE_IN_WEBVIEW = false;
const SHOW_APPLE_IN_WEBVIEW = true;
const SHOW_EMAIL = true;
const SHOW_RECAPTCHA_WARNING = false;

const isWebview = () => {
    return (ua.iOS && !isStandalone && !ua.Safari) || ua.WebView;
};

export default function RegistrationForm() {
    const isInWebview = isWebview();
    const recaptchaRef = useRef(null);
    const { t, i18n } = useTranslation('auth');
    const { api } = useActions();
    const initialEmail = useSearchParam('email');
    const hasInitialEmail = !!initialEmail;
    const [isEmailFormVisible, setIsEmailFormVisible] = useState(SHOW_EMAIL || hasInitialEmail || isInWebview);
    const isApp = useIsNativeApp();

    // const { code } = useParams();
    const formik = useEnhancedFormik({
        initialValues: {
            // code,
            email: initialEmail || '',
            password: '',
            lang: i18n.language,
            recaptchaToken: '',
        },
        onSubmit: async (payload) => {
            if (config.recaptcha_public_key) {
                await recaptchaRef.current.reset();
                payload = { ...payload, recaptchaToken: await recaptchaRef.current.executeAsync() };
            }
            const { redirect } = await api.users.create({ payload });
            window.location.replace(redirect || '/');
        },
    });

    const isSubmitEnabled = formik.values.email && formik.values.password;

    const emailErrors = [
        t(formik.errors.email),
        formik.submit.error?.code === 'GEOLOCKED' && t('auth.signup.geoLockedError'),
    ].filter(Boolean);

    const isGoogleVisible = SHOW_GOOGLE_IN_WEBVIEW || !isInWebview;
    const isAppleVisible = SHOW_APPLE_IN_WEBVIEW || !isInWebview;

    return (
        <form onSubmit={formik.handleSubmit}>
            {/*{pending && <Spinner />}*/}

            {/*<FormField*/}
            {/*    builderInput*/}
            {/*    type="hidden"*/}
            {/*    name="code"*/}
            {/*    errors={t(formik.errors.code)}*/}
            {/*    style={{ display: 'none' }}*/}
            {/*    errorStyle={{ fontSize: 16, textAlign: 'center' }}*/}
            {/*/>*/}

            <div>
                {isGoogleVisible && (
                    <GoogleLoginButton
                        beforeProvider="auth.signUpWith"
                        backgroundColor="#db4a39"
                        style={{ fontSize: 14 }}
                        id="button-signup-google"
                    />
                )}
                {isAppleVisible && (
                    <AppleLoginButton
                        beforeProvider="auth.signUpWith"
                        iconBorderColor="#222"
                        style={{ fontSize: 14 }}
                        id="button-signup-apple"
                    />
                )}
                {!isEmailFormVisible && (
                    <SocialLoginButton
                        icon={FiMail}
                        as="div"
                        onClick={() => setIsEmailFormVisible(true)}
                        style={{ fontSize: 14 }}
                        id="button-signup-show-email-form"
                    >
                        {t('auth.signUpWith', { provider: 'Email' })}
                    </SocialLoginButton>
                )}
                {isEmailFormVisible && (
                    <>
                        <FormField
                            builderInput
                            // disabled
                            placeholder={t('label.email')}
                            autoComplete="email"
                            autoFocus
                            errors={emailErrors}
                            {...formik.getFieldProps('email')}
                            style={{
                                height: 44,
                                fontSize: 18,
                                marginTop: isGoogleVisible || isAppleVisible ? 16 : 0,
                            }}
                            errorStyle={{ fontSize: 16, textAlign: 'center' }}
                        />
                        <FormField
                            builderInput
                            placeholder={t('label.password')}
                            type="password"
                            autoComplete="new-password"
                            errors={t(formik.errors.password)}
                            {...formik.getFieldProps('password')}
                            style={{ height: 44, fontSize: 18 }}
                            errorStyle={{ fontSize: 16, textAlign: 'center' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                fullWidth
                                type="submit"
                                isFetching={formik.isSubmitting}
                                disabled={!isSubmitEnabled}
                                style={{
                                    fontSize: 18,
                                    fontWeight: 'bold',
                                }}
                                id="button-signup-email"
                            >
                                {t('auth.signup.signup')}
                            </Button>
                        </div>
                        {!isApp && <LegaliseSection /> }

                        {config.recaptcha_public_key ? (
                            <span className={styles.recaptcha}>
                                This site is protected by reCAPTCHA and the Google{' '}
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </a>{' '}
                                and{' '}
                                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                                    Terms of Service
                                </a>{' '}
                                apply.
                            </span>
                        ) : (
                            SHOW_RECAPTCHA_WARNING && (
                                <span className={styles.recaptcha} style={{ color: 'red' }}>
                                    reCAPTCHA is DISABLED
                                </span>
                            )
                        )}
                    </>
                )}
            </div>
            {config.recaptcha_public_key && (
                <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={config.recaptcha_public_key} />
            )}
        </form>
    );
}
